import type { FC, KeyboardEvent, ReactNode } from 'react'

import { useRef } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CATEGORY_COLORS } from '@/constants/inventory'

interface ColorSwatchesProps {
    value: string
    onChange: (color: string) => void
    label: string
}

const COLOR_NAMES: Record<string, string> = {
    '#2563eb': 'Azul',
    '#0d9488': 'Verde azulado',
    '#d97706': 'Ámbar',
    '#9333ea': 'Violeta',
    '#db2777': 'Rosa',
    '#dc2626': 'Rojo',
    '#16a34a': 'Verde',
    '#475569': 'Gris pizarra'
}

const ColorSwatches: FC<ColorSwatchesProps> = ({ value, onChange, label }): ReactNode => {
    const refs = useRef<(HTMLButtonElement | null)[]>([])
    const selectedIndex = Math.max(CATEGORY_COLORS.indexOf(value.toLowerCase()), 0)

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
        let next = index

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % CATEGORY_COLORS.length
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + CATEGORY_COLORS.length) % CATEGORY_COLORS.length
        else return

        event.preventDefault()
        onChange(CATEGORY_COLORS[next])
        refs.current[next]?.focus()
    }

    return (
        <div role='radiogroup' aria-label={label} className='flex flex-wrap items-center gap-1.5'>
            {CATEGORY_COLORS.map((color, index) => {
                const selected = index === selectedIndex

                return (
                    <button
                        key={color}
                        ref={element => {
                            refs.current[index] = element
                        }}
                        type='button'
                        role='radio'
                        aria-checked={selected}
                        aria-label={COLOR_NAMES[color] ?? color}
                        tabIndex={selected ? 0 : -1}
                        onClick={() => onChange(color)}
                        onKeyDown={event => handleKeyDown(event, index)}
                        className={cn(
                            'flex size-7 items-center justify-center rounded-full outline-none transition-transform focus-visible:ring-3 focus-visible:ring-ring/50',
                            selected ? 'ring-2 ring-foreground/70 ring-offset-2 ring-offset-background' : 'hover:scale-110'
                        )}
                        style={{ backgroundColor: color }}
                    >
                        {selected && <Check className='size-3.5 text-white' aria-hidden='true' />}
                    </button>
                )
            })}
        </div>
    )
}

export default ColorSwatches
